import { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const pageStyles = {
  main: {
    width: 'min(1200px, calc(100% - 48px))',
    margin: '48px auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '32px'
  },
  hero: {
    background: 'linear-gradient(135deg, #fef6f2, #e9f0ff)',
    borderRadius: '32px',
    padding: '48px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    boxShadow: '0 25px 55px rgba(15, 23, 42, 0.08)'
  },
  heroTitle: {
    fontSize: '2.5rem',
    margin: 0
  },
  heroText: {
    margin: 0,
    color: '#4b5563',
    lineHeight: 1.6
  },
  highlightRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '18px'
  },
  highlightCard: {
    background: '#fff',
    borderRadius: '24px',
    padding: '24px',
    boxShadow: '0 20px 35px rgba(15, 23, 42, 0.06)'
  },
  contactGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '32px'
  },
  infoPanel: {
    background: '#fff',
    borderRadius: '28px',
    padding: '32px',
    boxShadow: '0 35px 50px rgba(15, 23, 42, 0.08)',
    display: 'flex',
    flexDirection: 'column',
    gap: '18px'
  },
  formPanel: {
    background: 'black',
    color: '#fff',
    borderRadius: '28px',
    padding: '32px',
    boxShadow: '0 35px 50px rgba(15, 23, 42, 0.15)',
    display: 'flex',
    flexDirection: 'column',
    gap: '18px'
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  input: {
    borderRadius: '16px',
    border: 'none',
    padding: '14px 18px',
    fontSize: '1rem'
  },
  textarea: {
    borderRadius: '16px',
    border: 'none',
    padding: '14px 18px',
    fontSize: '1rem',
    minHeight: '120px',
    resize: 'vertical'
  },
  button: {
    background: 'white',
    color: '#111827',
    border: 'none',
    borderRadius: '15px',
    padding: '16px 28px',
    fontWeight: 600,
    cursor: 'pointer'
  },
  status: {
    margin: 0,
    fontSize: '0.95rem',
    color: '#a7f3d0'
  }
};

const highlightData = [
  {
    title: 'Private Buyer Concierge',
    text: 'Tell us what view, location, and amenities you need and we shortlist villas and apartments for you.'
  },
  {
    title: 'Global Inventory',
    text: 'Access waterfront penthouses, countryside estates, and curated investment properties across 30+ cities.'
  },
  {
    title: 'Secure Transactions',
    text: 'Our legal partners streamline documentation, financing, and close so you can focus on moving in.'
  }
];

export default function ContactUsPage() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    budget: '',
    message: ''
  });
  const [status, setStatus] = useState('');

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setStatus('Request received! A Dream Home advisor will be in touch shortly.');
    setFormData({
      fullName: '',
      email: '',
      budget: '',
      message: ''
    });
  };

  return (
    <div className="contact-page">
      <Header />
      <main style={pageStyles.main}>
        <section style={pageStyles.hero}>
          <p className="hero-eyebrow">Contact us</p>
          <h1 style={pageStyles.heroTitle}>Speak with a buying expert</h1>
          <p style={pageStyles.heroText}>
            Whether you&apos;re eyeing a beachfront villa, a city penthouse, or a countryside retreat,
            our advisors pair you with the best listings and guide you through every step of the purchase.
          </p>
          <div style={pageStyles.highlightRow}>
            {highlightData.map((item) => (
              <div key={item.title} style={pageStyles.highlightCard}>
                <h3>{item.title}</h3>
                <p style={{ margin: 0, color: '#4b5563', lineHeight: 1.5 }}>{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section style={pageStyles.contactGrid}>
          <div style={pageStyles.infoPanel}>
            <h2>Visit or call</h2>
            <p style={{ margin: 0, color: '#4b5563' }}>
              Our showrooms are open daily for private appointments.
            </p>
            <div>
              <p style={{ margin: '0 0 4px', fontWeight: 600 }}>Global HQ</p>
              <p style={{ margin: 0, color: '#4b5563' }}>
                88 Harbor View Blvd<br />
                Miami, FL 33139
              </p>
            </div>
            <div>
              <p style={{ margin: '0 0 4px', fontWeight: 600 }}>Phone</p>
              <a href="tel:+18005551234" style={{ color: '#111827', textDecoration: 'none' }}>
                +1 (800) 555-1234
              </a>
            </div>
            <div>
              <p style={{ margin: '0 0 4px', fontWeight: 600 }}>Email</p>
              <a href="mailto:concierge@dreamhome.com" style={{ color: '#111827', textDecoration: 'none' }}>
                concierge@dreamhome.com
              </a>
            </div>
            <div>
              <p style={{ margin: '0 0 4px', fontWeight: 600 }}>Buying hours</p>
              <p style={{ margin: 0, color: '#4b5563' }}>Mon–Sat · 9am – 8pm EST</p>
            </div>
          </div>

          <form style={pageStyles.formPanel} onSubmit={handleSubmit}>
            <div>
              <p style={{ margin: '0 0 6px', opacity: 0.8 }}>Tell us about your dream property</p>
              <h2 style={{ margin: 0 }}>Request a call back</h2>
            </div>
            <div style={pageStyles.formField}>
              <label htmlFor="fullName">Full name</label>
              <input
                id="fullName"
                name="fullName"
                type="text"
                value={formData.fullName}
                onChange={handleChange}
                style={pageStyles.input}
                required
              />
            </div>
            <div style={pageStyles.formField}>
              <label htmlFor="email">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                style={pageStyles.input}
                required
              />
            </div>
            <div style={pageStyles.formField}>
              <label htmlFor="budget">Desired budget</label>
              <input
                id="budget"
                name="budget"
                type="text"
                value={formData.budget}
                onChange={handleChange}
                style={pageStyles.input}
                placeholder="$2,000,000"
              />
            </div>
            <div style={pageStyles.formField}>
              <label htmlFor="message">How can we help?</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                style={pageStyles.textarea}
                placeholder="I’m looking for a 3-bedroom beachfront villa in Europe..."
              />
            </div>
            <button type="submit" style={pageStyles.button}>
              Send request
            </button>
            {status && <p style={pageStyles.status}>{status}</p>}
          </form>
        </section>
      </main>
      <Footer />
    </div>
  );
}
